import { useState } from 'react';
import { useProgress } from '../../../../hooks/useProgress';

interface Vocabulary {
  term: string;
  definition: string;
}

interface Example {
  problem: string;
  steps: string[];
}

interface Question {
  id: number;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

interface Lesson {
  id: string;
  title: string;
  description: string;
  vocabulary: Vocabulary[];
  conceptExplanation: string;
  examples: Example[];
  teacherTip: string;
  questions: Question[];
}

interface LessonCardProps {
  lesson: Lesson;
}

export default function LessonCard({ lesson }: LessonCardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'practice'>('overview');
  const [expandedSteps, setExpandedSteps] = useState<{ [key: number]: number }>({});
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: number }>({});
  const [showExplanations, setShowExplanations] = useState<{ [key: number]: boolean }>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [filterMode, setFilterMode] = useState<'all' | 'correct' | 'incorrect'>('all');
  
  const { isCompleted, markComplete } = useProgress();
  const lessonCompleted = isCompleted(lesson.id);

  const toggleSteps = (exampleIndex: number) => {
    setExpandedSteps(prev => ({
      ...prev,
      [exampleIndex]: prev[exampleIndex] === lesson.examples[exampleIndex].steps.length 
        ? 0 
        : lesson.examples[exampleIndex].steps.length
    }));
  };

  const handleAnswerSelect = (questionId: number, answerIndex: number) => {
    if (!isSubmitted) {
      setSelectedAnswers(prev => ({
        ...prev,
        [questionId]: answerIndex
      }));
    }
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
  };

  const handleReset = () => {
    setSelectedAnswers({});
    setShowExplanations({});
    setIsSubmitted(false);
    setFilterMode('all');
  };

  const toggleExplanation = (questionId: number) => {
    setShowExplanations(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const calculateScore = () => {
    let correct = 0;
    lesson.questions.forEach(q => {
      if (selectedAnswers[q.id] === q.correctAnswer) {
        correct++;
      }
    });
    return { correct, total: lesson.questions.length };
  };

  const getFilteredQuestions = () => {
    if (!isSubmitted || filterMode === 'all') return lesson.questions;
    
    return lesson.questions.filter(q => {
      const isCorrect = selectedAnswers[q.id] === q.correctAnswer;
      return filterMode === 'correct' ? isCorrect : !isCorrect;
    });
  };

  const score = calculateScore();
  const filteredQuestions = getFilteredQuestions();

  const handleMarkComplete = () => {
    markComplete(lesson.id);
  };

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden transition-all ${lessonCompleted ? 'ring-2 ring-emerald-500' : ''}`}>
      {/* Card Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative">
        {lessonCompleted && (
          <div className="absolute top-4 right-4 bg-emerald-500 rounded-full w-10 h-10 flex items-center justify-center shadow-lg">
            <i className="ri-check-line text-xl"></i>
          </div>
        )}
        <h3 className="text-2xl font-bold mb-2">{lesson.title}</h3>
        <p className="text-indigo-100">{lesson.description}</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'overview'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-50'
            }`}
          >
            <i className="ri-book-open-line mr-2"></i>
            Lesson Overview
          </button>
          <button
            onClick={() => setActiveTab('practice')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'practice'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-50'
            }`}
          >
            <i className="ri-pencil-line mr-2"></i>
            Practice Questions
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' ? (
          <div className="space-y-8">
            {/* Vocabulary */}
            <div>
              <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <i className="ri-book-2-line text-indigo-600 mr-2"></i>
                Key Vocabulary
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {lesson.vocabulary.map((vocab, index) => (
                  <div
                    key={index}
                    className="group bg-gradient-to-br from-indigo-50 to-purple-50 p-4 rounded-lg border border-indigo-100 hover:shadow-md transition-all cursor-help"
                    title={vocab.definition}
                  >
                    <div className="font-bold text-indigo-700 mb-1">{vocab.term}</div>
                    <div className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                      {vocab.definition}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Concept Explanation */}
            <div>
              <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <i className="ri-lightbulb-line text-amber-500 mr-2"></i>
                Concept Explanation
              </h4>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {lesson.conceptExplanation}
                </p>
              </div>
            </div>

            {/* Worked Examples */}
            <div>
              <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <i className="ri-calculator-line text-emerald-600 mr-2"></i>
                Worked Examples
              </h4>
              <div className="space-y-4">
                {lesson.examples.map((example, index) => (
                  <div key={index} className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
                    <div className="font-semibold text-emerald-800 mb-3 flex items-start">
                      <span className="bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3 flex-shrink-0 mt-0.5">
                        {index + 1}
                      </span>
                      <span className="flex-1">{example.problem}</span>
                    </div>
                    <div className="ml-9 space-y-2">
                      {example.steps.slice(0, expandedSteps[index] || 0).map((step, stepIndex) => (
                        <div key={stepIndex} className="flex items-start text-sm text-gray-700 animate-fadeIn">
                          <i className="ri-arrow-right-s-line text-emerald-600 mr-2 flex-shrink-0 mt-0.5"></i>
                          <span>{step}</span>
                        </div>
                      ))}
                      <button
                        onClick={() => toggleSteps(index)}
                        className="mt-3 text-emerald-700 hover:text-emerald-800 font-semibold text-sm flex items-center transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {expandedSteps[index] === example.steps.length ? (
                          <>
                            <i className="ri-eye-off-line mr-1"></i>
                            Hide Steps
                          </>
                        ) : (
                          <>
                            <i className="ri-eye-line mr-1"></i>
                            Show {expandedSteps[index] ? 'More' : 'All'} Steps ({example.steps.length})
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Teacher Tip */}
            <div>
              <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <i className="ri-user-star-line text-blue-600 mr-2"></i>
                Mr. Augustine's Teaching Tip
              </h4>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <div className="flex items-start">
                  <div className="bg-blue-600 text-white rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 mr-4">
                    <i className="ri-lightbulb-flash-line text-lg"></i>
                  </div>
                  <p className="text-gray-700 leading-relaxed flex-1">{lesson.teacherTip}</p>
                </div>
              </div>
            </div>

            {/* Mark Complete Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleMarkComplete}
                disabled={lessonCompleted}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  lessonCompleted
                    ? 'bg-emerald-100 text-emerald-700 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md hover:shadow-lg'
                }`}
              >
                {lessonCompleted ? (
                  <>
                    <i className="ri-check-double-line mr-2"></i>
                    Lesson Completed
                  </>
                ) : (
                  <>
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    Mark as Complete
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Practice Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h4 className="text-lg font-bold text-gray-800">Practice Questions</h4>
                <p className="text-sm text-gray-600">{lesson.questions.length} questions total</p>
              </div>
              {!isSubmitted ? (
                <button
                  onClick={handleSubmit}
                  disabled={Object.keys(selectedAnswers).length === 0}
                  className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Submit Answers
                </button>
              ) : (
                <button
                  onClick={handleReset}
                  className="bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Reset Quiz
                </button>
              )}
            </div>

            {/* Score Display */}
            {isSubmitted && (
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="text-3xl font-bold">
                      {score.correct} / {score.total}
                    </div>
                    <div className="text-indigo-100">
                      {Math.round((score.correct / score.total) * 100)}% Correct
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setFilterMode('all')}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                        filterMode === 'all'
                          ? 'bg-white text-indigo-600'
                          : 'bg-indigo-500 text-white hover:bg-indigo-400'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setFilterMode('correct')}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                        filterMode === 'correct'
                          ? 'bg-white text-emerald-600'
                          : 'bg-indigo-500 text-white hover:bg-indigo-400'
                      }`}
                    >
                      Correct
                    </button>
                    <button
                      onClick={() => setFilterMode('incorrect')}
                      className={`px-4 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                        filterMode === 'incorrect'
                          ? 'bg-white text-red-600'
                          : 'bg-indigo-500 text-white hover:bg-indigo-400'
                      }`}
                    >
                      Incorrect
                    </button>
                  </div>
                </div>
                <div className="mt-4 bg-white/20 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-white h-full transition-all duration-500 rounded-full"
                    style={{ width: `${(score.correct / score.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Questions */}
            <div className="space-y-6">
              {filteredQuestions.map((question, index) => {
                const isAnswered = selectedAnswers[question.id] !== undefined;
                const isCorrect = selectedAnswers[question.id] === question.correctAnswer;
                
                return (
                  <div
                    key={question.id}
                    className={`border-2 rounded-lg p-6 transition-all ${
                      isSubmitted
                        ? isCorrect
                          ? 'border-emerald-500 bg-emerald-50'
                          : isAnswered
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300 bg-gray-50'
                        : 'border-gray-300 hover:border-indigo-300'
                    }`}
                  >
                    <div className="flex items-start mb-4">
                      <span className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                        {index + 1}
                      </span>
                      <p className="text-gray-800 font-medium flex-1">{question.question}</p>
                      {isSubmitted && (
                        <div className="ml-3">
                          {isCorrect ? (
                            <i className="ri-checkbox-circle-fill text-2xl text-emerald-600"></i>
                          ) : isAnswered ? (
                            <i className="ri-close-circle-fill text-2xl text-red-600"></i>
                          ) : (
                            <i className="ri-indeterminate-circle-fill text-2xl text-gray-400"></i>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2 ml-11">
                      {question.options.map((option, optionIndex) => {
                        const isSelected = selectedAnswers[question.id] === optionIndex;
                        const isCorrectOption = optionIndex === question.correctAnswer;
                        
                        return (
                          <button
                            key={optionIndex}
                            onClick={() => handleAnswerSelect(question.id, optionIndex)}
                            disabled={isSubmitted}
                            className={`w-full text-left p-4 rounded-lg border-2 transition-all whitespace-nowrap cursor-pointer ${
                              isSubmitted
                                ? isCorrectOption
                                  ? 'border-emerald-500 bg-emerald-100 font-semibold'
                                  : isSelected
                                  ? 'border-red-500 bg-red-100'
                                  : 'border-gray-200 bg-white'
                                : isSelected
                                ? 'border-indigo-600 bg-indigo-50 font-semibold'
                                : 'border-gray-300 bg-white hover:border-indigo-300 hover:bg-indigo-50'
                            } ${isSubmitted ? 'cursor-not-allowed' : ''}`}
                          >
                            <div className="flex items-center">
                              <span className="font-bold mr-3 text-gray-600">
                                {String.fromCharCode(65 + optionIndex)}.
                              </span>
                              <span className="flex-1">{option}</span>
                              {isSubmitted && isCorrectOption && (
                                <i className="ri-check-line text-emerald-600 text-xl ml-2"></i>
                              )}
                              {isSubmitted && isSelected && !isCorrectOption && (
                                <i className="ri-close-line text-red-600 text-xl ml-2"></i>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {isSubmitted && (
                      <div className="ml-11 mt-4">
                        <button
                          onClick={() => toggleExplanation(question.id)}
                          className="text-indigo-600 hover:text-indigo-700 font-semibold text-sm flex items-center transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className={`${showExplanations[question.id] ? 'ri-eye-off-line' : 'ri-eye-line'} mr-1`}></i>
                          {showExplanations[question.id] ? 'Hide' : 'Show'} Explanation
                        </button>
                        {showExplanations[question.id] && (
                          <div className="mt-3 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg animate-fadeIn">
                            <p className="text-sm text-gray-700 leading-relaxed">{question.explanation}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredQuestions.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
                <p className="text-gray-500 text-lg">No questions match this filter</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}